import { type ColumnDef } from '@tanstack/react-table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { type GalleryView } from '@/types';

interface UserColumnOptions<T> {
  id?: string;
  accessorFn?: (row: T) => any;
  accessorKey: keyof T;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  label: string;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  badgeClass?: string;
  dataCy?: string;
  galleryView?: GalleryView;
}

export const UserColumn = <T extends Record<string, any>>(opts: UserColumnOptions<T>): ColumnDef<T> => {
  const {
    id = 'createdBy',
    accessorFn,
    accessorKey,
    label,
    // Wide enough for an avatar + a truncated name/email under the fixed-layout table.
    // (Previously maxSize=30 clamped this to avatar-only and leaned on auto-layout
    // overflow to reveal the name — which breaks once the table is table-fixed.) (DM-2393)
    size = 160,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 30,
    maxSize,
    badgeClass,
    dataCy,
    galleryView,
  } = opts;

  return {
    id,
    enableSorting,
    ...(accessorFn && { accessorFn }),
    enableHiding,
    minSize,
    maxSize,
    accessorKey,
    header: () => label,
    meta: {
      ...(cellClassName && { cellClassName: cellClassName }),
      ...(headerClassName && { headerClassName: headerClassName }),
      ...(badgeClass && { badgeClass }),
    },
    size,
    cell: ({ getValue, row }) => {
      const user = getValue() as { name: string };

      if (!user) {
        return null;
      }

      return (
        <div
          className="flex min-w-0 items-center gap-2"
          data-cy={dataCy ?? `${galleryView ?? 'list'}-${row.index}-${id}`}
        >
          <Avatar className="size-7! max-h-7 min-h-7 max-w-7 min-w-7">
            <AvatarFallback className="text-xs font-bold text-text-gray">
              {user.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <p className="truncate text-text-gray">{user.name}</p>
        </div>
      );
    },
  };
};
